/**
 * Stripe支付测试工具
 * 用于在开发环境进行各类支付场景的测试
 */

// 导入Stripe元素
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';

// 测试是否启用
const isTestingEnabled = () => {
  return process.env.NODE_ENV !== 'production' 
      && process.env.NODE_ENV !== 'staging'
      && process.env.NODE_ENV !== undefined;
};

// 测试模式是否启用
const isTestMode = () => {
  return isTestingEnabled() && (process.env.STRIPE_TEST_MODE === 'true' || localStorage.getItem('stripe_test_mode') === 'true');
};

// Stripe测试卡号
const TEST_CARDS = {
  // 正常支付
  success: {
    number: '4242424242424242',
    expMonth: 12,
    expYear: 2028,
    cvc: '123',
    description: '正常支付成功'
  },
  // 需要额外验证 (3D Secure 2)
  requires3DS2: {
    number: '4000000000003220',
    expMonth: 12,
    expYear: 2028,
    cvc: '123',
    description: '需要3DS2验证'
  },
  // SCA验证失败
  authenticationFails: {
    number: '4000000000003063',
    expMonth: 12,
    expYear: 2028,
    cvc: '123',
    description: 'SCA验证失败'
  },
  // 余额不足
  insufficientFunds: {
    number: '4000000000009995',
    expMonth: 12,
    expYear: 2028,
    cvc: '123',
    description: '余额不足'
  },
  // 卡被拒绝
  cardDeclined: {
    number: '4000000000000002',
    expMonth: 12,
    expYear: 2028,
    cvc: '123',
    description: '卡被拒绝'
  },
  // 过期卡
  expired: {
    number: '4000000000000069',
    expMonth: 12,
    expYear: 2022,
    cvc: '123',
    description: '过期卡'
  },
  // 处理错误
  processingError: {
    number: '4000000000000119',
    expMonth: 12,
    expYear: 2028,
    cvc: '123',
    description: '处理错误'
  }
};

// 测试用例
const TEST_CASES = {
  // 基本支付测试用例
  basicPayment: {
    id: 'basic-payment',
    name: '基本支付流程',
    description: '测试普通卡支付流程',
    card: TEST_CARDS.success,
    saveCard: false
  },
  // 保存卡测试用例
  saveCard: {
    id: 'save-card',
    name: '保存卡片',
    description: '测试支付并保存卡片',
    card: TEST_CARDS.success,
    saveCard: true
  },
  // 使用已保存卡测试用例
  useSavedCard: {
    id: 'use-saved-card',
    name: '使用已保存卡',
    description: '测试使用已保存卡片支付',
    useExistingCard: true
  },
  // 3DS验证测试用例
  require3DSVerification: {
    id: '3ds-verification',
    name: '3DS验证',
    description: '测试需要3DS验证的支付流程',
    card: TEST_CARDS.requires3DS2,
    saveCard: false
  },
  // 卡被拒绝测试用例
  cardDeclined: {
    id: 'card-declined',
    name: '卡被拒绝',
    description: '测试卡被拒绝的情况',
    card: TEST_CARDS.cardDeclined,
    saveCard: false,
    expectError: true
  },
  // 保存卡数量限制测试用例
  cardLimitExceeded: {
    id: 'card-limit',
    name: '卡片数量限制',
    description: '测试保存卡数量达到上限的情况',
    card: TEST_CARDS.success,
    saveCard: true,
    setupFunc: async () => {
      // 模拟已有3张卡
      localStorage.setItem('mock_saved_cards_count', '3');
    },
    cleanupFunc: async () => {
      localStorage.removeItem('mock_saved_cards_count');
    }
  }
};

/**
 * 自动填充信用卡表单
 * @param {Object} elements - Stripe Elements实例
 * @param {Object} testCard - 测试卡信息
 * @param {string} cardholderName - 持卡人姓名
 */
const autoFillCardForm = async (elements, testCard, cardholderName = 'Test User') => {
  if (!isTestMode()) return;
  
  try {
    const cardNumber = elements.getElement(CardNumberElement);
    const cardExpiry = elements.getElement(CardExpiryElement);
    const cardCvc = elements.getElement(CardCvcElement);
    
    // 模拟填写卡号
    if (cardNumber) {
      await cardNumber._implementation._frame.send({
        action: 'input',
        payload: {
          type: 'change',
          value: testCard.number
        }
      });
      
      // 触发change事件
      cardNumber._implementation._emitEvent('change', {
        complete: true,
        value: {
          postalCode: ''
        }
      });
    }
    
    // 模拟填写过期日期
    if (cardExpiry) {
      await cardExpiry._implementation._frame.send({
        action: 'input',
        payload: {
          type: 'change',
          value: `${testCard.expMonth}/${testCard.expYear.toString().substr(-2)}`
        }
      });
      
      cardExpiry._implementation._emitEvent('change', {
        complete: true
      });
    }
    
    // 模拟填写CVC
    if (cardCvc) {
      await cardCvc._implementation._frame.send({
        action: 'input',
        payload: {
          type: 'change',
          value: testCard.cvc
        }
      });
      
      cardCvc._implementation._emitEvent('change', {
        complete: true
      });
    }
    
    // 返回模拟的持卡人姓名
    return cardholderName;
  } catch (error) {
    console.error('自动填充卡表单失败:', error);
    return null;
  }
};

/**
 * 运行指定的测试用例
 * @param {string} testCaseId - 测试用例ID
 * @param {Object} stripe - Stripe.js实例
 * @param {Object} elements - Stripe Elements实例
 * @param {Object} options - 额外参数
 */
const runTest = async (stripe, elements, testCaseKey, options = {}) => {
  if (!isTestMode()) return null;
  
  const testCase = TEST_CASES[testCaseKey];
  if (!testCase) {
    console.error(`[Stripe测试] 找不到测试用例: ${testCaseKey}`);
    return {
      success: false,
      error: `找不到测试用例: ${testCaseKey}`,
      testCase: testCaseKey
    };
  }
  
  console.log(`[Stripe测试] 开始测试: ${testCase.name}`);
  const result = {
    success: false,
    testCase: testCaseKey,
    name: testCase.name,
    logs: []
  };
  
  try {
    // 执行测试
    if (testCase.isSavedCard) {
      // 保存卡相关逻辑
      console.log('[Stripe测试] 使用保存的卡片');
      // 具体实现取决于应用的保存卡管理逻辑
    } else {
      // 填充表单
      console.log(`[Stripe测试] 自动填充卡表单: ${maskCardNumber(testCase.card.number)}`);
      const cardholderName = await autoFillCardForm(elements, testCase.card, options.cardholderName || 'Test User');
      
      // 创建支付方法
      console.log('[Stripe测试] 创建支付方法');
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: cardholderName || 'Test User'
        }
      });
      
      if (error) {
        console.error(`[Stripe测试] 创建支付方法失败: ${error.message}`);
        result.error = error.message;
        return result;
      }
      
      console.log('[Stripe测试] 创建支付方法成功:', paymentMethod.id);
      result.paymentMethodId = paymentMethod.id;
      
      // 确认支付意向
      if (options.confirmPaymentIntent && options.clientSecret) {
        console.log('[Stripe测试] 确认支付意向');
        const { error: confirmError } = await stripe.confirmCardPayment(
          options.clientSecret,
          {
            payment_method: paymentMethod.id
          }
        );
        
        if (confirmError) {
          console.error(`[Stripe测试] 确认支付意向失败: ${confirmError.message}`);
          result.error = confirmError.message;
          return result;
        }
        
        console.log('[Stripe测试] 确认支付意向成功');
      }
    }
    
    // 如果期望失败但实际成功
    if (testCase.expectError) {
      console.error(`[Stripe测试] 测试失败: 期望出错(${testCase.expectError})，但实际成功`);
      result.error = `期望出错(${testCase.expectError})，但实际成功`;
      return result;
    }
    
    // 测试成功
    console.log(`[Stripe测试] ${testCase.name} - 测试成功`);
    result.success = true;
    return result;
    
  } catch (error) {
    // 如果期望失败并且实际失败了
    if (testCase.expectError) {
      const errorMessage = error.message || String(error);
      if (errorMessage.includes(testCase.expectError)) {
        console.log(`[Stripe测试] ${testCase.name} - 测试成功: 预期的错误发生了`);
        result.success = true;
        result.expectedError = errorMessage;
        return result;
      }
    }
    
    // 意外错误
    console.error(`[Stripe测试] ${testCase.name} - 测试失败:`, error);
    result.error = error.message || String(error);
    return result;
  }
};

/**
 * 运行所有测试用例
 * @param {Object} stripe - Stripe实例
 * @param {Object} elements - Stripe Elements实例
 * @param {Object} options - 额外参数
 */
const runAllTests = async (stripe, elements, options = {}) => {
  if (!isTestMode()) return null;
  
  console.log('[Stripe测试] 开始运行所有测试');
  const results = [];
  
  for (const testCaseId in TEST_CASES) {
    const result = await runTest(stripe, elements, testCaseId, options);
    results.push(result);
  }
  
  console.log(`[Stripe测试] 所有测试完成: ${results.filter(r => r.success).length}/${results.length} 通过`);
  return results;
};

/**
 * 开启或关闭测试模式
 * @param {boolean} enable - 是否启用测试模式
 */
const setTestMode = (enable) => {
  if (enable) {
    localStorage.setItem('stripe_test_mode', 'true');
    console.log('Stripe测试模式已开启');
  } else {
    localStorage.removeItem('stripe_test_mode');
    console.log('Stripe测试模式已关闭');
  }
};

/**
 * 获取测试状态
 * @returns {Object} 测试状态
 */
const getTestStatus = () => {
  return {
    enabled: isTestMode(),
    isRunning: false,
    activeTest: null,
    results: []
  };
};

/**
 * 辅助函数 - 集成到已保存卡支付流程
 * @param {Object} savedCards - 保存的卡列表
 * @param {boolean} mockOffSessionFailure - 是否模拟离线会话失败
 */
const prepareSavedCardTest = (savedCards = [], mockOffSessionFailure = false) => {
  if (!isTestMode()) return savedCards;
  
  // 克隆已有卡列表
  const testCards = [...savedCards];
  
  // 如果模拟离线会话失败，添加一个标记到本地存储
  if (mockOffSessionFailure) {
    localStorage.setItem('stripe_mock_off_session_failure', 'true');
  } else {
    localStorage.removeItem('stripe_mock_off_session_failure');
  }
  
  // 如果卡列表为空，添加一些测试卡
  if (testCards.length === 0) {
    testCards.push({
      id: 'test_pm_success',
      brand: 'visa',
      last4: '4242',
      expMonth: 12,
      expYear: 2028,
      isDefault: true,
      isTestCard: true
    });
    
    testCards.push({
      id: 'test_pm_3ds',
      brand: 'visa',
      last4: '3220',
      expMonth: 12,
      expYear: 2028,
      isDefault: false,
      isTestCard: true
    });
  }
  
  return testCards;
};

/**
 * 创建一个可复用的测试面板React组件
 * @param {Object} React - React库实例
 * @returns {Function} 返回React组件
 */
const createTestPanel = (React) => {
  // 确保提供了React库
  if (!React) {
    console.error('创建TestPanel需要提供React库');
    return null;
  }
  
  // 创建测试面板组件
  return function TestPanel({ stripe, elements, options = {} }) {
    const [enabled, setEnabled] = React.useState(isTestMode());
    const [results, setResults] = React.useState([]);
    const [running, setRunning] = React.useState(false);
    const [currentTest, setCurrentTest] = React.useState(null);
    const [mockOffSessionFailure, setMockOffSessionFailure] = React.useState(
      localStorage.getItem('stripe_mock_off_session_failure') === 'true'
    );
    
    // 检查React版本
    const isReact18 = parseInt(React.version.split('.')[0]) >= 18;
    
    // 创建样式
    const styles = {
      container: {
        padding: '15px',
        backgroundColor: '#211c27',
        borderRadius: '8px',
        border: '1px solid #3a3643',
        margin: '20px 0',
        color: '#fff',
        fontFamily: 'Arial, sans-serif',
        display: process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging' ? 'none' : 'block'
      },
      title: {
        fontSize: '18px',
        fontWeight: 'bold',
        marginBottom: '15px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      },
      toggleButton: {
        backgroundColor: enabled ? '#6c63ff' : '#444',
        color: '#fff',
        border: 'none',
        padding: '5px 10px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px'
      },
      panel: {
        backgroundColor: '#2a2432',
        padding: '12px',
        borderRadius: '4px',
        marginTop: '10px'
      },
      section: {
        marginBottom: '15px'
      },
      sectionTitle: {
        fontSize: '14px',
        fontWeight: 'bold',
        marginBottom: '8px',
        color: '#ddd'
      },
      grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: '8px'
      },
      testButton: {
        backgroundColor: '#333',
        color: '#fff',
        border: '1px solid #555',
        padding: '8px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        textAlign: 'center'
      },
      selectedTestButton: {
        backgroundColor: '#6c63ff',
        borderColor: '#8a7dff'
      },
      controlButtons: {
        display: 'flex',
        gap: '10px',
        marginTop: '15px'
      },
      controlButton: {
        backgroundColor: '#333',
        color: '#fff',
        border: '1px solid #555',
        padding: '8px 12px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '13px'
      },
      results: {
        marginTop: '15px',
        backgroundColor: '#2a2432',
        borderRadius: '4px',
        padding: '10px',
        maxHeight: '200px',
        overflowY: 'auto'
      },
      resultItem: (success) => ({
        padding: '8px',
        marginBottom: '8px',
        borderLeft: `3px solid ${success ? '#4caf50' : '#f44336'}`,
        backgroundColor: '#27202e'
      }),
      resultTitle: (success) => ({
        color: success ? '#4caf50' : '#f44336',
        fontSize: '13px',
        fontWeight: 'bold',
        marginBottom: '4px'
      }),
      resultError: {
        color: '#f77',
        fontSize: '12px'
      },
      checkbox: {
        display: 'flex',
        alignItems: 'center',
        marginTop: '10px',
        color: '#aaa',
        fontSize: '13px'
      },
      checkboxInput: {
        marginRight: '8px'
      }
    };
    
    // 切换测试模式
    const toggleTestMode = () => {
      const newState = !enabled;
      setTestMode(newState);
      setEnabled(newState);
    };
    
    // 运行单个测试
    const runTestCase = async (testId) => {
      if (!stripe || !elements || running) return;
      
      setRunning(true);
      setCurrentTest(testId);
      
      try {
        const result = await runTest(
          stripe, 
          elements, 
          testId,
          {
            ...options,
            mockOffSessionFailure
          }
        );
        
        // 更新结果
        setResults(prev => [result, ...prev]);
      } catch (error) {
        console.error('运行测试出错:', error);
      } finally {
        setRunning(false);
        setCurrentTest(null);
      }
    };
    
    // 运行所有测试
    const runAllTestCases = async () => {
      if (!stripe || !elements || running) return;
      
      setRunning(true);
      setResults([]);
      
      try {
        const allResults = await runAllTests(
          stripe, 
          elements,
          {
            ...options,
            mockOffSessionFailure
          }
        );
        
        // 更新结果
        if (allResults && allResults.length > 0) {
          setResults(allResults);
        }
      } catch (error) {
        console.error('运行所有测试出错:', error);
      } finally {
        setRunning(false);
      }
    };
    
    // 清除结果
    const clearResults = () => {
      setResults([]);
    };
    
    // 处理离线会话模拟开关
    const toggleOffSessionFailure = (e) => {
      const checked = e.target.checked;
      setMockOffSessionFailure(checked);
      if (checked) {
        localStorage.setItem('stripe_mock_off_session_failure', 'true');
      } else {
        localStorage.removeItem('stripe_mock_off_session_failure');
      }
    };
    
    // 如果不是测试环境，不显示
    if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
      return null;
    }
    
    // 渲染组件
    return React.createElement(
      'div',
      { style: styles.container },
      // 标题栏
      React.createElement(
        'div',
        { style: styles.title },
        React.createElement('span', null, 'Stripe Payment Test Console'),
        React.createElement(
          'button',
          { style: styles.toggleButton, onClick: toggleTestMode },
          enabled ? 'Disable Testing' : 'Enable Testing'
        )
      ),
      
      // 测试面板内容 - 如果启用
      enabled && React.createElement(
        'div',
        { style: styles.panel },
        // 测试卡选择
        React.createElement(
          'div',
          { style: styles.section },
          React.createElement('div', { style: styles.sectionTitle }, 'Select Test:'),
          React.createElement(
            'div',
            { style: styles.grid },
            Object.entries(TEST_CASES).map(([key, test]) => 
              React.createElement(
                'button',
                { 
                  key,
                  style: { 
                    ...styles.testButton,
                    ...(currentTest === key ? styles.selectedTestButton : {})
                  },
                  disabled: running,
                  onClick: () => runTestCase(key)
                },
                test.name
              )
            )
          )
        ),
        
        // 离线会话模拟选项
        React.createElement(
          'div',
          { style: styles.checkbox },
          React.createElement(
            'input',
            {
              type: 'checkbox',
              style: styles.checkboxInput,
              checked: mockOffSessionFailure,
              onChange: toggleOffSessionFailure,
              id: 'mock-failure-checkbox'
            }
          ),
          React.createElement(
            'label',
            { htmlFor: 'mock-failure-checkbox' },
            'Mock saved card failure (to test graceful degradation)'
          )
        ),
        
        // 控制按钮
        React.createElement(
          'div',
          { style: styles.controlButtons },
          React.createElement(
            'button',
            { 
              style: styles.controlButton,
              disabled: running,
              onClick: runAllTestCases
            },
            'Run All Tests'
          ),
          React.createElement(
            'button',
            { 
              style: styles.controlButton,
              disabled: running || results.length === 0,
              onClick: clearResults
            },
            'Clear Results'
          ),
          running && React.createElement(
            'span',
            { style: { color: '#aaa', marginLeft: '10px', alignSelf: 'center' } },
            'Running tests...'
          )
        ),
        
        // 测试结果
        results.length > 0 && React.createElement(
          'div',
          { style: styles.results },
          results.map((result, index) => 
            React.createElement(
              'div',
              { key: index, style: styles.resultItem(result.success) },
              React.createElement(
                'div',
                { style: styles.resultTitle(result.success) },
                `${result.name || result.testCase}: ${result.success ? 'Success' : 'Failed'}`
              ),
              result.error && React.createElement(
                'div',
                { style: styles.resultError },
                `Error: ${result.error}`
              ),
              result.expectedError && React.createElement(
                'div',
                { style: { ...styles.resultError, color: '#4caf50' } },
                `Expected Error: ${result.expectedError}`
              )
            )
          )
        )
      )
    );
  };
};

/**
 * 屏蔽信用卡号，只显示前4位和后4位
 * @param {string} cardNumber - 完整信用卡号
 * @returns {string} 屏蔽后的信用卡号
 */
const maskCardNumber = (cardNumber) => {
  if (!cardNumber || cardNumber.length < 8) return cardNumber;
  return `${cardNumber.substr(0, 4)}XXXXXXXX${cardNumber.substr(-4)}`;
};

// 导出测试工具
export default {
  TEST_CARDS,
  TEST_CASES,
  isTestMode,
  setTestMode,
  runTest,
  runAllTests,
  getTestStatus,
  prepareSavedCardTest,
  createTestPanel
}; 